import { ToastContainer } from '@design-system/components/atoms'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { bindI18nBrand, initI18n } from '@mathflat/shared/i18n/i18n'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { domAnimation, LazyMotion } from 'framer-motion'
import { createBrowserHistory } from 'history'
import { syncHistoryWithStore } from 'mobx-react-router'
import ReactDOM from 'react-dom/client'
import { Router } from 'react-router-dom'
import smoothscroll from 'smoothscroll-polyfill'

// import { VWOProvider } from 'vwo-fme-react-sdk'
import { Portal } from '@/component/molecules/Portal'

import App from './App'
import authDaemon from './authDaemon'
import CommonModal from './component/templates/ModalTemplate/Modal'
import { getInitialUserContext, vwoConfig } from './hooks/useSetVwoUserContext'
import commonRepo from './repository/common.repo'
import { RouteName } from './router'
import { localStorageService } from './service/storage.service'
import { envUtils } from './utils'
import { installBrowserTranslationGuard } from './utils/browserTranslationGuard'
import { datadogClient } from './utils/modules/datadog/datadogClient'
import navigationUtils from './utils/navigationUtils'
import { testUtils } from './utils/testUtils'
import { mixpanelClient } from './utils/trackings/mixpanel'

smoothscroll.polyfill()

installBrowserTranslationGuard()

domainProvider.debugging({
  isLiveEnv: import.meta.env.VITE_VERCEL_ENV === 'production',
})

// Datadog은 에러 로깅을 위해 즉시 초기화
datadogClient.initialize()

// Mixpanel은 나중에 초기화 (성능 최적화)
const initializeMixpanel = () => {
  mixpanelClient.initialize()
}

if (typeof requestIdleCallback !== 'undefined') {
  requestIdleCallback(initializeMixpanel, { timeout: 2000 })
} else {
  setTimeout(initializeMixpanel, 1)
}

if (!envUtils.isLive) {
  testUtils.initialize()
}

const history = syncHistoryWithStore(createBrowserHistory(), commonRepo.router)
const clientSideEmotionCache = createCache({ key: 'css' })
const queryClient = new QueryClient()

if (localStorageService.getItem('LOGIN_PATH') === null) {
  const isLoginPath =
    window.location.pathname === RouteName.login ||
    window.location.pathname === RouteName.busanLogin ||
    window.location.pathname === RouteName.jeonnamLogin ||
    window.location.pathname === RouteName.publicLogin

  localStorageService.setItem('LOGIN_PATH')(
    isLoginPath
      ? (window.location.pathname as
          | typeof RouteName.login
          | typeof RouteName.busanLogin
          | typeof RouteName.jeonnamLogin
          | typeof RouteName.publicLogin)
      : RouteName.login,
  )
}
commonRepo.router.listener.listen()

authDaemon()
  .then(() => initI18n())
  .then(() => {
    const academy = commonRepo.academy
    if (!academy) return
    return bindI18nBrand(
      { academyGroupType: academy.academyGroupType, customerType: academy.customerType },
      academy.languagePackFileUrl,
    )
  })
  .then(() => {
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
      <Router history={history}>
        <CacheProvider value={clientSideEmotionCache}>
          <QueryClientProvider client={queryClient}>
            {/* <VWOProvider config={vwoConfig} userContext={getInitialUserContext()}> */}
            <LazyMotion features={domAnimation}>
              <App />
            </LazyMotion>
            <CommonModal />
            <ToastContainer />
            {/* </VWOProvider> */}
          </QueryClientProvider>
          <Portal.Container />
        </CacheProvider>
      </Router>,
    )
  })
  .catch((error) => {
    // requestIdleCallback으로 초기화되므로 약간의 지연 후 사용 가능
    setTimeout(() => datadogClient.sendError(error), 100)
    console.error('App initialization error:', error)
  })

// 일렉트론 401 Error 발생시 사용할 함수 - 추후 localStorageService 키와 같게 수정해야함.
window['invalidateToken'] = function () {
  window.location.href = RouteName.login
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('x-auth-token')
  localStorage.removeItem('teacher-id')
  localStorage.removeItem('teacherId')
  sessionStorage.removeItem('accessToken')
  sessionStorage.removeItem('refreshToken')
  sessionStorage.removeItem('x-auth-token')
  sessionStorage.removeItem('teacher-id')
  sessionStorage.removeItem('teacherId')
  navigationUtils.reload()
}
